class ConstructorUtils {
  /**
   * List of product handles that should be excluded from displaying
   * swatches and badges in Constructor.io results (e.g., gift cards)
   */
  static EXCLUDED_PRODUCT_HANDLES = ["evry-gift-card"];
  static EXCLUDED_PRODUCT_IDS = ["6886660505677"];

  /**
   * Check if a product should be excluded from swatches and badges
   * Used for gift cards and similar products where swatches/badges don't make sense
   * @param {object} product - Product data
   * @returns {boolean} True if product should be excluded
   */
  static isExcludedProduct(product) {
    if (!product) return false;

    const handle = product.handle || "";
    const productId = String(
      product.id || product.data?.id || product.constructorData?.data?.id || product.constructorData?.id || ""
    );

    return this.EXCLUDED_PRODUCT_HANDLES.includes(handle) || this.EXCLUDED_PRODUCT_IDS.includes(productId);
  }

  /**
   * Face-out selection: prefer the first variation flagged replenishable.
   *
   * @param {object} item - Raw Constructor.io result
   * @returns {object} The item with `data` merged from the chosen variation, or the
   *                   same item when nothing qualifies. Never mutates the response.
   */
  static withReplenishableFaceOut(item) {
    const variations = item?.variations;
    if (!Array.isArray(variations) || !variations.length) return item;

    if (item.data?.replenishable === true) return item;

    const match = variations.find((variation) => variation?.data?.replenishable === true);
    if (!match) return item;
    if (match.data.variation_id && match.data.variation_id === item.data?.variation_id) return item;

    return { ...item, data: { ...item.data, ...match.data } };
  }

  /**
   * Format money value from Constructor.io API (already in dollars)
   * This method converts and delegates to Shopify's native formatter for consistency
   *
   * @param {number|string} dollars - Amount in dollars (not cents) from Constructor.io
   * @param {string|object} options - Format string or options object
   * @returns {string} Formatted money string (e.g., "$19.99")
   */
  static formatMoney(dollars, options = {}) {
    // Parse input
    if (typeof dollars === "string") {
      dollars = parseFloat(dollars);
    }

    if (isNaN(dollars) || dollars == null) return "$0.00";

    // Handle options
    if (typeof options === "string") {
      options = { format: options };
    }

    if (options.withCurrency) {
      return this.formatMoneyWithCurrency(dollars);
    }

    const cents = Math.round(dollars * 100);

    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      const format = options.format || window.Shopify.money_format;
      return window.Shopify.formatMoney(cents, format);
    }

    return `$${dollars.toFixed(2)}`;
  }

  /**
   * Format money with currency symbol using Intl.NumberFormat
   * @param {number} dollars - Amount in dollars (not cents)
   * @returns {string} Formatted money with currency
   */
  static formatMoneyWithCurrency(dollars) {
    const amount = Number(dollars);
    const currencyCode = window.theme?.fakeDiscount?.currentMarket?.currency || "USD";

    try {
      const formatted = new Intl.NumberFormat(window.Shopify?.locale || "en-US", {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);

      // Remove redundant currency suffixes (CA, US, USD, CAD) if present
      return formatted.replace(/\s?(CA|US|USD|CAD)$/g, "");
    } catch (error) {
      return this.formatMoney(amount);
    }
  }

  /**
   * Build sized image URL with Shopify CDN query parameters
   * @param {string} url - Original image URL
   * @param {number} width - Target width
   * @param {number} height - Target height
   * @returns {string} Transformed image URL
   */
  static buildSizedImageUrl(url, width, height) {
    if (!url) return "";

    if (url.includes("width=") && url.includes("height=")) {
      return url;
    }

    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}width=${width}&height=${height}&crop=center`;
  }

  /**
   * Get theme configuration value by key
   * @param {string} key - Configuration key
   * @returns {any} Configuration value
   */
  static getThemeConfig(key) {
    if (!key || !window.theme) return null;

    const keys = key.split(".");
    let value = window.theme;

    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = value[k];
      } else {
        return null;
      }
    }

    return value;
  }

  /**
   * Process media images from product data
   * @param {object} product - Product object
   * @returns {Array} Processed media images array
   */
  static processMediaImages(product) {
    if (Array.isArray(product.images) && product.images.length > 0) {
      return product.images;
    }

    const constructorData = product.constructorData?.data || product.constructorData || {};
    const rawMedia = constructorData.mediaImages;

    if (Array.isArray(rawMedia)) {
      return rawMedia;
    }

    return rawMedia ? [rawMedia] : [];
  }

  /**
   * Get image dimensions based on orientation setting
   * @param {string} orientation - Image orientation (square, landscape, portrait)
   * @param {object} options - Additional options (defaultWidth, defaultHeight)
   * @returns {object} Width and height dimensions
   */
  static getImageDimensions(orientation = "square", options = {}) {
    const { defaultWidth = 600, defaultHeight = 600 } = options;

    switch (orientation) {
      case "landscape":
        return { width: defaultWidth, height: Math.round(defaultWidth * 0.8) };
      case "portrait":
        return {
          width: Math.round(defaultHeight * 0.8),
          height: defaultHeight,
        };
      case "square":
      default:
        return { width: defaultWidth, height: defaultHeight };
    }
  }

  /**
   * Validate product object for required fields
   * @param {object} product - Product object to validate
   * @returns {object} Validation result with isValid and errors
   */
  static validateProduct(product) {
    if (!product) {
      return { isValid: false, errors: ["Product is null or undefined"] };
    }

    const errors = [];

    if (!product.title && !product.handle) {
      errors.push("Product missing title and handle");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Normalize color names for consistent comparison
   * @param {string} colorName - Raw color name
   * @returns {string}
   */
  static normalizeColorName(colorName) {
    if (!colorName || typeof colorName !== "string") return "";

    return colorName
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]/g, "");
  }

  /**
   * Convert an option value into a Shopify `handleize`d slug.
   * Must stay equivalent to Liquid's `handleize` filter: swatch PNGs are uploaded
   * under handleized names (e.g. "Waterproof - Silver" -> "waterproof-silver"),
   * so any divergence here 404s the swatch image.
   * @param {string} value - Raw option value
   * @returns {string}
   */
  static handleizeColorName(value) {
    if (!value || typeof value !== "string") return "";

    return value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  /**
   * Generate variant swatches from Constructor.io product data
   * @param {object} product - Product data from Constructor.io
   * @param {object} options - Options for swatch generation
   * @returns {object} Swatches result with HTML and metadata
   */
  static generateVariantSwatches(product, options = {}) {
    // Skip swatches for excluded products (e.g., gift cards)
    if (this.isExcludedProduct(product)) {
      return { shouldShow: false, swatchesHTML: null };
    }

    const swatchSettings = this.getThemeConfig("variantSwatches");

    if (!swatchSettings?.enabled) {
      return { shouldShow: false, swatchesHTML: null };
    }

    const variations = product.variants || product.variations || [];
    if (!variations || variations.length === 0) {
      return { shouldShow: false, swatchesHTML: null };
    }

    const uniqueColors = new Map();
    const imgOrientation = this.getThemeConfig("productCard.imgOrientation") || "square";
    const dimensions = this.getImageDimensions(imgOrientation);
    const constructorData = product.constructorData?.data || product.constructorData || product.data || {};
    const mediaImages = Array.isArray(constructorData.mediaImages) ? constructorData.mediaImages : [];

    variations.forEach((variation) => {
      const data = variation.data || variation;
      let color = data.color || data.colour || data.metal || variation.option1;

      if (!color) {
        return;
      }

      if (!uniqueColors.has(color)) {
        let rawImageUrl = data.image_url || data.imageUrl || "";

        if (!rawImageUrl && mediaImages.length > 0) {
          const variantImage = mediaImages.find((img) => img.variant_ids?.includes(data.variation_id || data.id));
          rawImageUrl = variantImage?.transformedSrc || variantImage?.src || "";
        }

        if (!rawImageUrl && mediaImages.length > 0) {
          rawImageUrl = mediaImages[0]?.transformedSrc || mediaImages[0]?.src || "";
        }

        const sizedImageUrl = rawImageUrl ? this.buildSizedImageUrl(rawImageUrl, dimensions.width, dimensions.height) : "";

        uniqueColors.set(color, {
          color: color,
          image_url: sizedImageUrl,
          inventory_quantity: data.inventory_quantity || 0,
          inventory_policy: data.inventory_policy || data.inventoryPolicy,
          variation_id: data.variation_id || data.id,
          title: data.title,
          available:
            data.inventory_policy === "continue" ||
            data.inventory_policy === "CONTINUE" ||
            data.inventoryPolicy === "continue" ||
            data.inventoryPolicy === "CONTINUE" ||
            (data.inventory_quantity || 0) > 0,
          // Price data for dynamic price updates
          price: data.price || 0,
          compare_at_price: data.compareAtPrice || data.compare_at_price || 0,
          discount_percentage: data.discountPercentage || data.discount_percentage || 0,
        });
      }
    });

    const colorArray = Array.from(uniqueColors.keys());
    if (colorArray.length === 0) {
      return {
        shouldShow: true,
        swatchesHTML: `<div class="swatches-container"><div class="swatches-spacer" style="height: 24px;"></div></div>`,
        totalColors: 0,
        visibleColors: 0,
      };
    }

    const maxVisible = swatchSettings.maxVisible || 4;
    const useVariantImages = swatchSettings.useVariantImages || false;

    let swatchesHTML = `
      <div class="swatches-container">
        <ul class="color-swatches list-unstyled mx-0 mb-0" role="list" aria-label="Color options">
    `;

    colorArray.slice(0, maxVisible).forEach((color) => {
      const colorData = uniqueColors.get(color);

      swatchesHTML += `
        <li class="p-0" role="listitem">
          <button class="btn${colorData.available ? "" : " swatch-unavailable"}" 
                  type="button"
                  data-img="${colorData.image_url || ""}"
                  data-img-width="${dimensions.width}"
                  data-img-height="${dimensions.height}"
                  data-variant-available="${colorData.available}"
                  data-color-value="${color}"
                  data-price="${colorData.price || 0}"
                  data-compare-at-price="${colorData.compare_at_price || 0}"
                  data-discount-percentage="${colorData.discount_percentage || 0}"
                  data-action="change-swatch"
                  aria-label="Select ${color} color option"
                  title="${color} - ${colorData.image_url ? "Has image" : "No image"}">
      `;

      if (useVariantImages && colorData.image_url) {
        swatchesHTML += `
          <img class="img-fluid swatch-variant-image"
               src="${colorData.image_url}"
               alt="${color}"
               width="40" height="40"
               loading="lazy" aria-hidden="true">
        `;
      } else {
        const swatchImageName = this.handleizeColorName(color);
        const shopifySwatchUrl = `https://cdn.shopify.com/s/files/1/0262/9903/9821/files/swatch-${swatchImageName}.png?width=24&height=24&crop=center`;

        swatchesHTML += `
          <img class="img-fluid swatch-custom-image"
               src="${shopifySwatchUrl}"
               alt="${color}"
               width="24" height="24"
               loading="lazy" aria-hidden="true"
               onerror="this.style.display='none'">
        `;
      }

      swatchesHTML += `
          </button>
        </li>
      `;
    });

    if (colorArray.length > maxVisible) {
      const remainingCount = colorArray.length - maxVisible;
      swatchesHTML += `
        <li class="color-swatch-more" role="listitem">
          <button type="button" 
                  class="btn btn-outline-secondary btn-sm rounded-circle d-flex align-items-center justify-content-center"
                  style="width: 32px; height: 32px; font-size: 10px;"
                  title="Show ${remainingCount} more colors"
                  aria-label="Show ${remainingCount} more color options"
                  data-action="show-all-swatches">
            +${remainingCount}
          </button>
        </li>
      `;
    }

    swatchesHTML += `
        </ul>
      </div>
    `;

    return {
      shouldShow: true,
      swatchesHTML: swatchesHTML,
      totalColors: colorArray.length,
      visibleColors: Math.min(colorArray.length, maxVisible),
    };
  }

  /**
   * Generate product badges using Constructor.io data
   * @param {object} product - Product data from Constructor.io
   * @param {boolean} hasDiscount - Whether product has a discount
   * @returns {object} Badge result with HTML and metadata
   */
  static generateProductBadge(product, hasDiscount = false) {
    // Check for gift card badge first (special handling for gift card product)
    // A family flagged "show above product card" leaves the in-card chain entirely, so
    // the next family in the priority order can still take the in-card slot.
    const giftCardBadgeResult = this._checkGiftCardBadge(product);
    if (giftCardBadgeResult.shouldShow && !this.getThemeConfig("giftCardBadge")?.aboveCard) {
      return giftCardBadgeResult;
    }

    // Skip other badges for excluded products (e.g., gift cards)
    if (this.isExcludedProduct(product)) {
      return { shouldShow: false, badgeHTML: null, badgeType: "excluded-product" };
    }

    const data = product.constructorData?.data || product.constructorData || product.data || {};
    // Get tags from multiple possible sources
    const rawTags = product.tags || product.constructorData?.data?.tags || product.data?.tags || [];
    const tags = Array.isArray(rawTags) ? rawTags : typeof rawTags === "string" ? rawTags.split(",").map((t) => t.trim()) : [];
    const customBadgeSettings = this.getThemeConfig("customBadge");
    if (customBadgeSettings?.globalEnabled && customBadgeSettings?.marketSettings?.enabled && !customBadgeSettings?.aboveCard) {
      // Check for required tag filter
      const requiredTag = customBadgeSettings.marketSettings.tag;
      const hasRequiredTag =
        !requiredTag || requiredTag.trim() === "" || tags.some((tag) => tag.toLowerCase() === requiredTag.toLowerCase().trim());

      if (hasRequiredTag) {
        return this._generateCustomBadge(customBadgeSettings.marketSettings, customBadgeSettings.position);
      }
    }

    if (data.preorder_tag) {
      const preorderSettings = this.getThemeConfig("preorderBadge");
      if (preorderSettings?.enabled && !preorderSettings?.aboveCard) {
        return this._generatePreorderBadge(preorderSettings);
      }
      if (preorderSettings?.enabled && preorderSettings?.aboveCard) {
        // Pre-order products still suppress the rest of the chain, as before.
        return { shouldShow: false, badgeHTML: null, badgeType: "preorder-above-card" };
      }
      return {
        shouldShow: false,
        badgeHTML: null,
        badgeType: "preorder-disabled",
      };
    }

    if (data.new_preorder_tag) {
      const newPreorderSettings = this.getThemeConfig("newPreorderBadge");
      if (newPreorderSettings?.enabled && !newPreorderSettings?.aboveCard) {
        return this._generateNewPreorderBadge(newPreorderSettings);
      }
    }

    const promoBadgeSettings = this.getThemeConfig("promoBadge");
    if (promoBadgeSettings?.enabled && hasDiscount && !promoBadgeSettings?.aboveCard) {
      return this._generatePromoBadge(product, promoBadgeSettings);
    }

    const commercialBadgeSettings = this.getThemeConfig("commercialBadge");
    if (commercialBadgeSettings?.enabled && !commercialBadgeSettings?.aboveCard) {
      return this._generateCommercialBadge(data, commercialBadgeSettings);
    }

    return { shouldShow: false, badgeHTML: null, badgeType: "none" };
  }

  /**
   * Generate commercial badge using Constructor.io data
   */
  static _generateCommercialBadge(data, settings) {
    let badgeType = null;
    let badgeSettings = null;

    if (data.waterproof_tag) {
      // Waterproof still wins the chain — so it keeps shadowing new / bestseller /
      // low inventory exactly as before — but its label now renders inline above the
      // card title via buildWaterproofTag(), so it contributes no badge here.
      // Liquid mirror: the `cb_type != 'waterproof'` guard in commercial-badge.liquid.
      return { shouldShow: false, badgeHTML: null, badgeType: "commercial-waterproof-inline" };
    } else if (data.new_tag) {
      badgeType = "new";
      badgeSettings = settings.new;
    } else if (data.bestseller_tag) {
      badgeType = "bestseller";
      badgeSettings = settings.bestseller;
    } else if (data.low_inventory_tag) {
      badgeType = "lowInventory";
      badgeSettings = settings.lowInventory;
    }

    if (!badgeType || !badgeSettings?.text) {
      return {
        shouldShow: false,
        badgeHTML: null,
        badgeType: "commercial-no-match",
      };
    }

    const position = settings.position || "top";
    const badgeHTML = `
      <div class="badge-wrapper badge-wrapper--commercial badge-position-${position}">
        <span class="badge badge--commercial badge--${badgeType}"
              style="background-color: ${badgeSettings.bgColor}; 
                     color: ${badgeSettings.textColor}; 
                     font-size: ${settings.fontSize}px; 
                     border-radius: ${settings.borderRadius}px; 
                     opacity: ${settings.opacity / 100}; 
                     padding: ${settings.padding}px; 
                     width: ${settings.width}%; 
                     height: ${settings.height}%;">
          ${badgeSettings.text}
        </span>
      </div>
    `;

    return {
      shouldShow: true,
      badgeHTML,
      badgeText: badgeSettings.text,
      badgeType: `commercial-${badgeType}`,
    };
  }

  /**
   * Generate promo badge based on discount percentage
   */
  static _generatePromoBadge(product, settings) {
    const data = product.constructorData?.data || product.constructorData || product.data || {};
    const compareAtPrice = data.compareAtPrice || 0;
    const price = data.price || 0;

    if (!compareAtPrice || compareAtPrice <= price) {
      return { shouldShow: false, badgeHTML: null, badgeType: "no-discount" };
    }

    const savingsPercentage = Math.round(((compareAtPrice - price) * 100) / compareAtPrice);
    const remainder = savingsPercentage % 5;
    const roundedPercentage = remainder > 2 ? savingsPercentage + 5 - remainder : savingsPercentage - remainder;

    const opacity = (settings.opacity || 100) / 100;
    const bgColor = settings.bgColor || "#ff0000";
    const rgbMatch = bgColor.match(/^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    const r = rgbMatch ? parseInt(rgbMatch[1], 16) : 255;
    const g = rgbMatch ? parseInt(rgbMatch[2], 16) : 0;
    const b = rgbMatch ? parseInt(rgbMatch[3], 16) : 0;

    const position = settings.position || "top";
    const badgeHTML = `
      <div class="badge-wrapper badge-wrapper--promo badge-position-${position}">
        <span class="badge badge--promo"
              style="padding: ${settings.padding || 8}px;
                     width: ${settings.width || 60}px;
                     height: ${settings.height || 60}px;
                     font-size: ${settings.fontSize || 12}px;
                     background-color: rgba(${r}, ${g}, ${b}, ${opacity});
                     color: ${settings.textColor || "#ffffff"};
                     border-radius: ${settings.borderRadius || 50}px;">
          ${roundedPercentage}% OFF
        </span>
      </div>
    `;

    return {
      shouldShow: true,
      badgeHTML,
      badgeText: `${roundedPercentage}% OFF`,
      badgeType: "promo",
      discountPercentage: roundedPercentage,
    };
  }

  /**
   * Generate custom badge
   */
  static _generateCustomBadge(settings, position = "top") {
    const badgePosition = position || "top";
    const badgeHTML = `
      <div class="badge-wrapper badge-wrapper--custom badge-position-${badgePosition}">
        <span class="badge badge--custom"
              style="background-color: ${settings.bgColor}; 
                     color: ${settings.textColor}; 
                     font-size: ${settings.fontSize}px; 
                     border-radius: ${settings.borderRadius}px; 
                     opacity: ${settings.opacity / 100}; 
                     padding: ${settings.padding}px; 
                     width: ${settings.width}px; 
                     height: ${settings.height}px;">
          ${settings.text}
        </span>
      </div>
    `;

    return { shouldShow: true, badgeHTML, badgeText: settings.text };
  }

  /**
   * Generate pre-order badge
   */
  static _generatePreorderBadge(settings) {
    const opacity = (settings.opacity || 100) / 100;
    const position = settings.position || "top";
    const badgeHTML = `
      <div class="badge-wrapper badge-wrapper--preorder badge-position-${position}">
        <span class="badge badge--preorder"
              style="background-color: ${settings.bgColor}; 
                     color: ${settings.textColor}; 
                     border-radius: ${settings.borderRadius}px; 
                     opacity: ${opacity}; 
                     padding: ${settings.padding}px;">
          ${settings.text}
        </span>
      </div>
    `;

    return { shouldShow: true, badgeHTML, badgeText: settings.text, badgeType: "preorder" };
  }

  /**
   * Generate new pre-order badge
   */
  static _generateNewPreorderBadge(settings) {
    const opacity = (settings.opacity || 100) / 100;
    const position = settings.position || "top";
    const badgeHTML = `
      <div class="badge-wrapper badge-wrapper--new-preorder badge-position-${position}">
        <span class="badge badge--new-preorder"
              style="background-color: ${settings.bgColor}; 
                     color: ${settings.textColor}; 
                     border-radius: ${settings.borderRadius}px; 
                     opacity: ${opacity}; 
                     padding: ${settings.padding}px;">
          ${settings.text}
        </span>
      </div>
    `;

    return { shouldShow: true, badgeHTML, badgeText: settings.text, badgeType: "new-preorder" };
  }

  /**
   * Check and generate gift card badge for product ID 6886660505677
   * @param {object} product - Product data from Constructor.io
   * @returns {object} Badge result with HTML and metadata
   */
  static _checkGiftCardBadge(product) {
    const GIFT_CARD_PRODUCT_ID = "6886660505677";

    // Get product ID from various possible sources
    const productId = String(
      product.id || product.data?.id || product.constructorData?.data?.id || product.constructorData?.id || ""
    );

    // Check if this is the gift card product
    if (productId !== GIFT_CARD_PRODUCT_ID) {
      return { shouldShow: false, badgeHTML: null, badgeType: "not-gift-card" };
    }

    // Get gift card badge settings from theme
    const giftCardBadgeSettings = this.getThemeConfig("giftCardBadge");

    if (!giftCardBadgeSettings?.enabled) {
      return { shouldShow: false, badgeHTML: null, badgeType: "gift-card-badge-disabled" };
    }

    const opacity = (giftCardBadgeSettings.opacity || 100) / 100;
    const position = giftCardBadgeSettings.position || "top";

    const badgeHTML = `
      <div class="badge-wrapper badge-wrapper--gift-card gift-card-badge-wrapper badge-position-${position}">
        <span class="badge badge--gift-card gift-card-badge"
              style="background-color: ${giftCardBadgeSettings.bgColor || "#D4AF37"}; 
                     color: ${giftCardBadgeSettings.textColor || "#FFFFFF"}; 
                     font-size: ${giftCardBadgeSettings.fontSize || 12}px;
                     border-radius: ${giftCardBadgeSettings.borderRadius || 4}px; 
                     opacity: ${opacity}; 
                     padding: ${giftCardBadgeSettings.padding || 6}px;
                     width: ${giftCardBadgeSettings.width || 100}px;
                     height: ${giftCardBadgeSettings.height || 30}px;
                     display: inline-flex;
                     align-items: center;
                     justify-content: center;
                     font-weight: 500;
                     text-transform: uppercase;
                     line-height: 1.2;">
          ${giftCardBadgeSettings.text || "GIFT CARD"}
        </span>
      </div>
    `;

    return { shouldShow: true, badgeHTML, badgeText: giftCardBadgeSettings.text || "GIFT CARD", badgeType: "gift-card" };
  }

  /**
   * Resolve which markdown slot applies to a product.
   * theme.liquid emits window.theme.fakeDiscount.slots already filtered to the current
   * market's enabled, tagged slots, in priority order. The first slot whose tag the
   * product carries wins — discounts never stack.
   * @param {object} product - Product data from Constructor.io
   * @returns {object|null} The winning slot config, or null when nothing applies
   */
  static resolveFakeDiscountSlot(product) {
    const fakeDiscountSettings = this.getThemeConfig("fakeDiscount");
    if (!fakeDiscountSettings?.enabled) return null;

    const slots = Array.isArray(fakeDiscountSettings.slots) ? fakeDiscountSettings.slots : [];
    if (!slots.length) return null;

    const productTags = product.tags || product.constructorData?.data?.tags || product.data?.tags || [];
    const tagsArray = Array.isArray(productTags)
      ? productTags
      : typeof productTags === "string"
      ? productTags.split(",").map((t) => t.trim())
      : [];
    if (!tagsArray.length) return null;

    const normalizedTags = tagsArray.map((tag) => (typeof tag === "string" ? tag.toLowerCase() : ""));

    // A slot lists one or more tags, matched as OR. `slot.tag` is the pre-multi-tag
    // shape, kept as a fallback so a stale theme.liquid degrades instead of going dark.
    return (
      slots.find((slot) => {
        const slotTags = Array.isArray(slot?.tags) ? slot.tags : slot?.tag ? [slot.tag] : [];
        return slotTags.some((tag) => tag && normalizedTags.includes(String(tag).trim().toLowerCase()));
      }) || null
    );
  }

  /**
   * Generate fake discount message from Constructor.io product data
   * Applies the winning slot's discount, using that slot's own priceSource
   * @param {object} product - Product data from Constructor.io
   * @param {number} compareAtPrice - Compare at price from Constructor
   * @param {number} price - Current price from Constructor
   * @returns {object} Fake discount result with HTML and adjusted prices
   */
  static generateFakeDiscountMessage(product, compareAtPrice, price) {
    const noDiscount = {
      shouldShow: false,
      messageHTML: null,
      discountPercentage: 0,
      adjustedPrice: price,
      adjustedComparePrice: compareAtPrice,
    };

    const slot = this.resolveFakeDiscountSlot(product);
    if (!slot) return noDiscount;

    const priceSource = slot.priceSource || "price";
    let basePrice = price;

    if (priceSource !== "price" && compareAtPrice && compareAtPrice > 0) {
      basePrice = compareAtPrice;
    }

    // Mirror snippets/fake-discount-system.liquid exactly so a Constructor card and its
    // PDP never disagree: work in whole cents, truncate the percentage discount the way
    // Liquid's integer `divided_by: 100` does, then round the result up to the next 10c.
    const baseCents = Math.round(basePrice * 100);
    const discountType = slot.type || "percentage";

    let discountedCents = baseCents;
    let displayPercentage = 0;

    if (discountType === "fixed") {
      const fixedAmount = parseFloat(slot.fixedAmount || 0);
      if (fixedAmount > 0) {
        discountedCents = Math.max(1, baseCents - Math.round(fixedAmount * 100));
        displayPercentage = baseCents > 0 ? Math.round(((baseCents - discountedCents) * 100) / baseCents) : 0;
      }
    } else {
      const percentage = slot.percentage || 0;
      if (percentage > 0) {
        discountedCents = baseCents - Math.floor((baseCents * percentage) / 100);
        discountedCents = Math.ceil(discountedCents / 10) * 10;
        displayPercentage = percentage;
      }
    }

    const discountedPrice = discountedCents / 100;

    if (discountedPrice >= basePrice) return noDiscount;

    // [percentage] renders the configured percentage and [percentage-amount] the
    // effective one, matching the Liquid placeholder semantics.
    const message = String(slot.message || "")
      .replace(/\[percentage\]/g, slot.percentage ?? "")
      .replace(/\[percentage-amount\]/g, displayPercentage)
      .replace(/\[amount\]/g, slot.fixedAmount || "0")
      .replace(/\[code\]/g, slot.code || "");

    const color = slot.messageColor || "#dc3545";
    const messageHTML = message
      ? `
      <div class="fake-discount-message mt-2 p-2 rounded-2"
           style="color: ${color}; font-size: 0.875rem; line-height: 1.2;">
        ${message}
      </div>
    `
      : null;

    return {
      // A blank message still marks the price down; it just has nothing to render.
      shouldShow: Boolean(messageHTML),
      messageHTML,
      discountPercentage: displayPercentage,
      adjustedPrice: discountedPrice,
      adjustedComparePrice: priceSource === "price" ? compareAtPrice : basePrice,
    };
  }

  /**
   * Generate product promo text based on product tags
   * Checks if product has any of the configured promo text tags and returns matching text
   * @param {object} product - Product data from Constructor.io
   * @returns {object} Promo text result with shouldShow and promoTextHTML
   */
  static getProductPromoText(product) {
    const promoTextSettings = this.getThemeConfig("promoText");

    // Moved above the card: buildCardLabelBar renders it, so it must not render here too.
    if (!promoTextSettings?.enabled || promoTextSettings?.aboveCard) {
      return { shouldShow: false, promoTextHTML: "" };
    }

    const slots = promoTextSettings.slots || [];
    if (slots.length === 0) {
      return { shouldShow: false, promoTextHTML: "" };
    }

    const productTags = product.tags || product.constructorData?.data?.tags || product.data?.tags || [];
    const tagsArray = Array.isArray(productTags)
      ? productTags
      : typeof productTags === "string"
      ? productTags.split(",").map((t) => t.trim().toLowerCase())
      : [];

    const normalizedTags = tagsArray.map((t) => (typeof t === "string" ? t.toLowerCase() : ""));

    for (const slot of slots) {
      if (!slot.tag || slot.tag.trim() === "") continue;

      const slotTag = slot.tag.toLowerCase().trim();
      const hasTag = normalizedTags.some((tag) => tag === slotTag);

      if (hasTag) {
        const locale = promoTextSettings.locale || "en";
        const text = locale === "fr" ? slot.textFr || slot.textEn : slot.textEn;

        if (!text || text.trim() === "") continue;

        const fontSize = slot.fontSize || 11;
        const promoTextHTML = `
          <div class="product-promo-text-tag rounded"
               style="
                 background-color: ${slot.bgColor || "#ee1882"};
                 color: ${slot.textColor || "#ffffff"};
                 font-size: ${fontSize}px;
                 font-weight: 600;
                 padding: 1px;
                 letter-spacing: 0.5px;
                 line-height: 1.2;
               ">
            ${this.sanitizeHTML(text)}
          </div>
        `;

        return { shouldShow: true, promoTextHTML };
      }
    }

    return { shouldShow: false, promoTextHTML: "" };
  }

  /**
   * Build the label bar shown ABOVE a product card.
   *
   * Collects every label family the merchant flagged "Show above product card"
   * (Theme settings > Product cards). A family collected here is skipped in the in-card
   * chain by generateProductBadge, so a label is never rendered twice.
   *
   * Plus the six standalone "Labels by tag" slots (Theme settings > Label bar), which
   * live only in this bar and each carry their own on/off toggle. They are the one
   * family that can contribute more than one item.
   *
   * Presentation:
   *   0 items -> an empty bar of the same height, so image tops stay aligned in a row
   *   1 item  -> static, no animation
   *   2+      -> the group is emitted twice and scrolls continuously (CSS handles the
   *              separators, including the seam between the two groups)
   *
   * Liquid mirror: snippets/product-card-labels.liquid — keep the order and the
   * matching rules in sync. Styles: assets/sections.css (.tag-label-marquee).
   *
   * @param {object} product - Product data from Constructor.io
   * @param {object} [options] - { hasDiscount, salePercentage } from the card's pricing pass
   * @returns {string} HTML string, or "" when nothing is configured to show up here
   */
  static buildCardLabelBar(product, options = {}) {
    const tagLabelConfig = this.getThemeConfig("tagLabel") || {};
    const data = product?.constructorData?.data || product?.constructorData || product?.data || {};
    // Same normalization the rest of this file uses: lowercase + trimmed, exact match.
    const rawTags = product?.tags || product?.constructorData?.data?.tags || product?.data?.tags || [];
    const tagsArray = Array.isArray(rawTags) ? rawTags : typeof rawTags === "string" ? rawTags.split(",") : [];
    const normalizedTags = tagsArray.map((tag) => (typeof tag === "string" ? tag.trim().toLowerCase() : ""));

    // The bar is a single strip: an item contributes its text and text colour, and the
    // first one also decides the colour of the whole bar.
    const items = [];
    const push = (text, bg, color) => {
      if (!text || String(text).trim() === "") return;
      items.push({ text: this.sanitizeHTML(String(text)), bg: bg || "#ee1882", color: color || "#ffffff" });
    };

    // --- same order as the Liquid collector ---

    const giftCard = this.getThemeConfig("giftCardBadge");
    if (giftCard?.enabled && giftCard?.aboveCard) {
      const productId = String(product?.id || product?.data?.id || product?.constructorData?.data?.id || "");
      if (productId === "6886660505677") {
        push(giftCard.text, giftCard.bgColor, giftCard.textColor);
      }
    }

    const customBadge = this.getThemeConfig("customBadge");
    if (customBadge?.aboveCard && customBadge?.globalEnabled && customBadge?.marketSettings?.enabled) {
      const requiredTag = customBadge.marketSettings.tag;
      const hasRequiredTag =
        !requiredTag || requiredTag.trim() === "" || normalizedTags.includes(requiredTag.toLowerCase().trim());
      if (hasRequiredTag) {
        const m = customBadge.marketSettings;
        push(m.text, m.bgColor, m.textColor);
      }
    }

    const preorder = this.getThemeConfig("preorderBadge");
    if (preorder?.enabled && preorder?.aboveCard && data.preorder_tag) {
      push(preorder.text, preorder.bgColor, preorder.textColor);
    }

    const newPreorder = this.getThemeConfig("newPreorderBadge");
    if (newPreorder?.enabled && newPreorder?.aboveCard && data.new_preorder_tag) {
      push(newPreorder.text, newPreorder.bgColor, newPreorder.textColor);
    }

    const promoBadge = this.getThemeConfig("promoBadge");
    if (promoBadge?.enabled && promoBadge?.aboveCard) {
      const compareAtPrice = data.compareAtPrice || 0;
      const price = data.price || 0;
      if (compareAtPrice > price && price > 0) {
        const pct = Math.round(((compareAtPrice - price) * 100) / compareAtPrice);
        const remainder = pct % 5;
        const rounded = remainder > 2 ? pct + 5 - remainder : pct - remainder;
        push(`${rounded}% OFF`, promoBadge.bgColor, promoBadge.textColor);
      }
    }

    const commercial = this.getThemeConfig("commercialBadge");
    if (commercial?.enabled && commercial?.aboveCard && !data.preorder_tag) {
      // Same priority as _generateCommercialBadge: waterproof > new > bestseller > low inventory
      let slot = null;
      // Waterproof resolves here and leaves `slot` null on purpose: it keeps shadowing
      // the labels below it, but renders inline above the card title instead of in this
      // bar. Liquid mirror: the `cb_type != 'waterproof'` guard in commercial-badge.liquid.
      if (data.waterproof_tag) slot = null;
      else if (data.new_tag) slot = commercial.new;
      else if (data.bestseller_tag) slot = commercial.bestseller;
      else if (data.low_inventory_tag) slot = commercial.lowInventory;
      if (slot) push(slot.text, slot.bgColor, slot.textColor);
    }

    const productCard = this.getThemeConfig("productCard");
    if (productCard?.showSaleBadge && productCard?.saleBadgeAboveCard && options.hasDiscount && options.salePercentage) {
      push(`${options.salePercentage}% OFF`, "#dc3545", "#ffffff");
    }

    const promoText = this.getThemeConfig("promoText");
    if (promoText?.enabled && promoText?.aboveCard) {
      for (const slot of promoText.slots || []) {
        if (!slot.tag || slot.tag.trim() === "") continue;
        if (!normalizedTags.includes(slot.tag.toLowerCase().trim())) continue;
        const text = (promoText.locale || "en") === "fr" ? slot.textFr || slot.textEn : slot.textEn;
        if (!text || text.trim() === "") continue;
        push(text, slot.bgColor, slot.textColor);
        break; // getProductPromoText stops at the first match; stay consistent
      }
    }

    // Bar-only family: the six standalone "Labels by tag" slots. theme.liquid emits a
    // slot only when it is enabled and has a tag, so presence here means active.
    // Every match contributes an item — no break — unlike promoText above.
    const tagSlots = tagLabelConfig.slots || [];
    for (const slot of tagSlots) {
      if (!slot.tag || slot.tag.trim() === "") continue;
      if (!normalizedTags.includes(slot.tag.toLowerCase().trim())) continue;
      const text = (tagLabelConfig.locale || "en") === "fr" ? slot.textFr || slot.textEn : slot.textEn;
      if (!text || text.trim() === "") continue;
      push(text, slot.bgColor, slot.textColor);
    }

    const anyFlag =
      giftCard?.aboveCard ||
      customBadge?.aboveCard ||
      preorder?.aboveCard ||
      newPreorder?.aboveCard ||
      promoBadge?.aboveCard ||
      commercial?.aboveCard ||
      productCard?.saleBadgeAboveCard ||
      promoText?.aboveCard ||
      tagSlots.length > 0;

    if (!anyFlag) return "";

    const height = tagLabelConfig.height || 22;
    const speed = tagLabelConfig.speed || 12;
    const align = tagLabelConfig.align || "center";
    const separator = tagLabelConfig.separator || "\u00b7";

    if (items.length === 0) {
      return `<div class="tag-label-marquee tag-label-marquee--empty" style="--tl-height:${height}px;" aria-hidden="true"></div>`;
    }

    const group = items
      .map((item) => `<span class="tag-label-marquee__item" style="color:${item.color};">${item.text}</span>`)
      .join("");
    const isStatic = items.length === 1;
    const barBg = items[0].bg;

    return `
      <div class="tag-label-marquee${isStatic ? " tag-label-marquee--static" : ""}" style="--tl-height:${height}px;--tl-speed:${speed}s;--tl-align:${align};--tl-sep:'${separator}';background-color:${barBg};">
        <div class="tag-label-marquee__track">
          <div class="tag-label-marquee__group">${group}</div>
          ${isStatic ? "" : `<div class="tag-label-marquee__group" aria-hidden="true">${group}</div>`}
        </div>
      </div>
    `;
  }

  /**
   * The waterproof label, rendered as a plain text line in the card body directly above
   * the title. On mobile the card's children are flex-ordered image -> ATC -> body, so
   * this lands below the Add to Cart button; on desktop the ATC is a hover overlay
   * inside .img-wrapper, so it lands below that. Either way: below the ATC, above the
   * title.
   *
   * This is the ONLY place waterproof renders now — _generateCommercialBadge() and
   * buildCardLabelBar() both resolve it and emit nothing, which preserves its position
   * in the shared priority chain (preorder > waterproof > new > bestseller > low
   * inventory) without showing it twice.
   *
   * Liquid mirror: snippets/commercial-badge.liquid `mode: 'inline'` — keep the enable
   * gate, the preorder shadow and the text source in sync.
   *
   * @param {object} product - Product data from Constructor.io
   * @returns {string} HTML string, or "" when the product is not waterproof
   */
  static buildWaterproofTag(product) {
    const commercial = this.getThemeConfig("commercialBadge");
    if (!commercial?.enabled) return "";

    const data = product?.constructorData?.data || product?.constructorData || product?.data || {};
    // Preorder outranks waterproof in the chain, so a preorder product shows neither.
    if (data.preorder_tag || !data.waterproof_tag) return "";

    const text = commercial.waterproof?.text;
    if (!text || String(text).trim() === "") return "";

    // No inline colour: waterproof.textColor is picked to sit on the badge/bar
    // background (#FFFFFF today) and would be invisible on the bare card. The tag
    // inherits the card's text colour from assets/product.css instead.
    return `<span class="product-item-waterproof-tag d-block">${this.sanitizeHTML(String(text))}</span>`;
  }

  /**
   * Get current market data from theme configuration
   * @returns {object} Current market configuration
   */
  static getCurrentMarketData() {
    const fakeDiscountSettings = this.getThemeConfig("fakeDiscount") || {};

    return {
      currency: fakeDiscountSettings.currentMarket?.currency || "USD",
      locale: fakeDiscountSettings.currentMarket?.locale || "en-US",
      market: fakeDiscountSettings.currentMarket || {},
    };
  }

  /**
   * Get cached theme configuration optimized for product cards
   * @returns {object} Cached theme configuration
   */
  static getCachedThemeConfig() {
    if (this._themeConfigCache) return this._themeConfigCache;

    const theme = window.theme || {};
    const productCard = theme.productCard || {};
    const fakeDiscount = theme.fakeDiscount || {};
    const promoText = theme.promoText || {};

    this._themeConfigCache = {
      imgOrientation: productCard.imgOrientation || "square",
      cardStyle: productCard.style,
      spacingMobile: productCard.spacing?.mobile ? `p-${productCard.spacing.mobile}` : "p-3",
      spacingDesktop: productCard.spacing?.desktop ? `p-lg-${productCard.spacing.desktop}` : "p-lg-4",
      imgThumbnail: productCard.imgThumbnail || "",
      titleFontSizeMobile: productCard.titleFontSizeMobile || "fs-6",
      titleFontSizeDesktop: productCard.titleFontSizeDesktop || "fs-lg-6",
      titleTruncate: productCard.titleTruncate || "",
      showRating: productCard.showRating,
      showWishlistBtn: productCard.showWishlistBtn,
      showAtcForm: productCard.showAtcForm,
      showSaleBadge: productCard.showSaleBadge,
      saleBadgeColor: productCard.saleBadgeColor || "primary",
      wishlistEnable: theme.wishlist?.enable,
      wishlistAddText: theme.wishlist?.addText || "Add to wishlist",
      wishlistRemoveText: theme.wishlist?.removeText || "Remove from wishlist",
      productPriceRegular: theme.product?.priceRegular || "Regular price",
      productPriceSale: theme.product?.priceSale || "Sale price",
      productCompValue: theme.product?.compValue || "Comp. value",
      priceDisplayStyle: theme.product?.priceDisplayStyle || "comp_value",
      productSave: theme.product?.save || "Save",
      // Message and colour are per-slot now, so there is no single value to cache here.
      fakeDiscountEnabled: fakeDiscount.enabled || false,
      promoText: {
        enabled: promoText.enabled || false,
        locale: promoText.locale || "en",
        slots: promoText.slots || [],
      },
    };

    switch (this._themeConfigCache.imgOrientation) {
      case "landscape":
        this._themeConfigCache.imgWidth = 600;
        this._themeConfigCache.imgHeight = 480;
        this._themeConfigCache.aspectRatio = "5/4";
        break;
      case "portrait":
        this._themeConfigCache.imgWidth = 480;
        this._themeConfigCache.imgHeight = 600;
        this._themeConfigCache.aspectRatio = "4/5";
        break;
      default:
        this._themeConfigCache.imgWidth = 600;
        this._themeConfigCache.imgHeight = 600;
        this._themeConfigCache.aspectRatio = "1/1";
    }

    return this._themeConfigCache;
  }

  /**
   * Render a complete product card HTML
   * Unified template for search, browse, and recommendations
   * @param {object} product - Product data from Constructor.io
   * @param {object} options - Rendering options
   * @returns {string} Complete product card HTML
   */
  static renderProductCard(product, options = {}) {
    if (!product) return "";

    const config = options.config || this._getDefaultConfig();
    const { imgWidth, imgHeight, aspectRatio } = config;

    const productId =
      product.data?.id ||
      product.data?.data?.id ||
      product.id ||
      product.constructorData?.data?.id ||
      product.constructorData?.id ||
      "";

    const productTitle = product.title || product.value || "";
    const productHandle = product.handle || "";
    const productUrl = product.url || product.constructorData?.url || `/products/${productHandle}`;
    const firstVariant = (product.variants && product.variants[0]) || {};
    const constructorData = product.constructorData?.data || product.constructorData || {};
    const rawPrimarySrc =
      constructorData.image_url || constructorData.mediaImages?.[0]?.transformedSrc || product.featured_image?.src || "";
    const rawSecondarySrc = constructorData.mediaImages?.[1]?.transformedSrc || product.images?.[1]?.transformedSrc || null;
    const mediaImages = constructorData.mediaImages || product.images || [];

    const productIndex = options.productIndex || 0;
    const ABOVE_FOLD_COUNT = 6;
    const isAboveTheFold = productIndex < ABOVE_FOLD_COUNT;

    const getBaseUrl = (url) => {
      if (!url) return "";
      const qIndex = url.indexOf("?");
      return qIndex > 0 ? url.substring(0, qIndex) : url;
    };

    const primaryBaseUrl = getBaseUrl(rawPrimarySrc);
    const secondaryBaseUrl = getBaseUrl(rawSecondarySrc);

    /**
     * Generate responsive srcset with multiple image sizes
     * Based on Shopify best practices for image optimization
     */
    const generateResponsiveImage = (baseUrl, maxWidth, maxHeight) => {
      if (!baseUrl) return { src: "", srcset: "", sizes: "" };

      const widths = [100, 200, 300, 400, 600, 800, 1000];
      const validWidths = widths.filter((w) => w <= maxWidth);

      if (!validWidths.includes(maxWidth)) {
        validWidths.push(maxWidth);
        validWidths.sort((a, b) => a - b);
      }

      const srcsetEntries = validWidths.map((width) => {
        const height = Math.round((width / maxWidth) * maxHeight);
        return `${baseUrl}?width=${width}&height=${height} ${width}w`;
      });

      const src = `${baseUrl}?width=${maxWidth}&height=${maxHeight}`;
      const srcset = srcsetEntries.join(", ");
      const sizes = `(max-width: 480px) 400px, (max-width: 768px) 600px, (max-width: 992px) 600px, (max-width: 1200px) 800px, ${maxWidth}px`;

      return { src, srcset, sizes };
    };

    const primaryImage = generateResponsiveImage(primaryBaseUrl, imgWidth, imgHeight);
    const secondaryImage = generateResponsiveImage(secondaryBaseUrl, imgWidth, imgHeight);

    const primarySrc = primaryImage.src;
    const primarySrcset = primaryImage.srcset;
    const primarySizes = primaryImage.sizes;

    const secondarySrc = secondaryImage.src;
    const secondarySrcset = secondaryImage.srcset;
    const secondarySizes = secondaryImage.sizes;

    const loadingStrategy = isAboveTheFold ? "eager" : "lazy";
    const HIGH_PRIORITY_COUNT = 2;
    const fetchPriority = isAboveTheFold && productIndex < HIGH_PRIORITY_COUNT ? "high" : "auto";
    // SEO fallback: genuine admin alt wins; otherwise "<title> - <type> | EVRYJEWELS"
    // (type segment omitted when blank). featured_image.alt is intentionally excluded
    // because browse/search transforms pre-fill it with the bare title.
    const productType = product.product_type || constructorData.product_type || "";
    const imageAlt = this.sanitizeHTML(
      mediaImages[0]?.altText ||
        `${productTitle}${productType ? " - " + productType : ""} | EVRYJEWELS`
    ).replace(/"/g, "&quot;"); // sanitizeHTML escapes &<> but not quotes; guard the attribute
    const basePrice = product.price || constructorData.price || firstVariant.price || 0;
    const baseComparePrice = product.compare_at_price || constructorData.compareAtPrice || firstVariant.compare_at_price || 0;
    const fakeDiscountResult = this.generateFakeDiscountMessage(product, baseComparePrice, basePrice);
    const discountPercentage = fakeDiscountResult.discountPercentage;
    const finalPrice = fakeDiscountResult.adjustedPrice;
    const originalPrice = fakeDiscountResult.adjustedComparePrice;
    const isOnSale = originalPrice && originalPrice > finalPrice;
    const promoTextResult = this.getProductPromoText(product);

    const primaryBadgeResult = this.generateProductBadge(product, isOnSale, discountPercentage > 0);
    const showSaleBadge = config.showSaleBadge && isOnSale;
    const saleBadgeColor = config.saleBadgeColor;
    const swatchesResult = this.generateVariantSwatches(product);
    const formattedPrice = this.formatMoney(finalPrice);
    const formattedComparePrice = isOnSale ? this.formatMoney(originalPrice) : "";
    const marginClass = config.cardStyle !== "card" ? "mb-0" : "";

    const badgePosition = this._getBadgePosition(product, isOnSale, config);
    const wishlistPosition = badgePosition === "bottom" ? "bottom-0 end-0" : "bottom-0 start-0";

    const strategyId = product.strategy?.id || product.strategy || "";
    const position = options.index !== undefined ? options.index + 1 : 0;

    // Generate badge HTML for both mobile and desktop
    const badgeHTML = primaryBadgeResult.shouldShow
      ? primaryBadgeResult.badgeHTML
      : showSaleBadge && discountPercentage > 0
      ? `<span class="img-badge-sale badge ${saleBadgeColor} text-white position-absolute">
           ${config.productSave} ${discountPercentage}%
         </span>`
      : "";

    // Extract badge text for mobile label display
    const badgeText = primaryBadgeResult.shouldShow
      ? primaryBadgeResult.badgeText || ""
      : showSaleBadge && discountPercentage > 0
      ? `${config.productSave} ${discountPercentage}%`
      : "";

    return `
      <li class="product-item splide__slide ${config.spacingMobile} ${config.spacingDesktop} ${marginClass}" 
          role="listitem"
          data-product-handle="${productHandle}"
          data-cnstrc-item="recommendation"
          data-cnstrc-item-id="${productId}"
          data-cnstrc-item-name="${productTitle}"
          data-cnstrc-item-variation-id="${firstVariant.id || ""}"
          data-cnstrc-strategy-id="${strategyId}"
          data-cnstrc-item-price="${finalPrice}"
          data-cnstrc-item-section="Products"
          data-cnstrc-position="${position}">
        ${this.buildCardLabelBar(product, { hasDiscount: isOnSale, salePercentage: discountPercentage })}
        <div class="${config.cardStyle || ""}">
          <div class="mobile-badge-above-image d-block d-lg-none${badgeText ? " has-badge" : ""}">${badgeText || "&nbsp;"}</div>
          <div class="img-wrapper position-relative mb-4${secondarySrc ? " has-hover-image" : ""}${
      badgeText ? " has-badge-above" : ""
    }">
            <a href="${productUrl}">
              <div class="desktop-badge-wrapper d-none d-lg-block">
                ${badgeHTML}
              </div>
              ${
                primarySrc
                  ? `
                <img 
                  src="${primarySrc}"
                  srcset="${primarySrcset}"
                  sizes="${primarySizes}"
                  alt="${imageAlt}"
                  class="product-item-img product-item-img--primary img-fluid rounded ${config.imgThumbnail} png-background"
                  width="${imgWidth}"
                  height="${imgHeight}"
                  loading="${loadingStrategy}"
                  fetchpriority="${fetchPriority}"
                  decoding="async"
                  style="aspect-ratio: ${aspectRatio};"
                />
                ${
                  secondarySrc
                    ? `
                <img 
                  src="${secondarySrc}"
                  srcset="${secondarySrcset}"
                  sizes="${secondarySizes}"
                  alt="${imageAlt}"
                  class="product-item-img product-item-img--secondary img-fluid ${config.imgThumbnail} png-background"
                  width="${imgWidth}"
                  height="${imgHeight}"
                  loading="lazy"
                  fetchpriority="low"
                  decoding="async"
                  style="aspect-ratio: ${aspectRatio};"
                />`
                    : ""
                }
              `
                  : `
                <div class="placeholder-image bg-light d-flex align-items-center justify-content-center" 
                     style="aspect-ratio: ${aspectRatio}; min-height: 200px;">
                  <span class="text-muted">No Image</span>
                </div>
              `
              }
            </a>

            ${
              config.wishlistEnable && config.showWishlistBtn
                ? `
            <button class="btn-wishlist-add-remove btn position-absolute ${wishlistPosition} m-2" 
                    type="button"
                    data-product-handle="${productHandle}"
                    data-text-add="${config.wishlistAddText}"
                    data-text-remove="${config.wishlistRemoveText}"
                    aria-label="${config.wishlistAddText}"
                    onclick="addOrRemoveFromWishlist(this)"
                    data-cnstrc-btn="add_to_wishlist"
                    style="z-index: 1;">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" 
                   fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </button>
            `
                : ""
            }

            ${options.renderAddToCartDesktop ? options.renderAddToCartDesktop(product) : ""}
          </div>
          
          <div class="firstline-container">
            <div class="${config.cardStyle === "card" ? "card-body" : ""}">
              ${this.buildWaterproofTag(product)}
              <h3 class="product-item-title mb-2 ${config.titleFontSizeMobile} ${config.titleFontSizeDesktop} ${
      config.titleTruncate
    }">
                <a href="${productUrl}" class="product-link position-relative d-block link-dark" tabindex="-1">
                  ${productTitle}
                </a>
              </h3>
              
              ${config.showRating ? `<div class="product-rating-badge mb-2"></div>` : ""}
              
              <p class="product-item-price product-item-price-carrossel" data-product-item-price>
              ${
                isOnSale
                  ? config.priceDisplayStyle === "strikethrough"
                    ? `
                      <span class="product-item-price-compare text-muted me-1">
                        <span class="visually-hidden">${config.productPriceRegular}&nbsp;</span>
                        <s>${formattedComparePrice}</s>
                      </span>
                      <span class="product-item-price-final">
                        <span class="visually-hidden">${config.productPriceSale}&nbsp;</span>
                        ${formattedPrice}
                      </span>
                    `
                    : `
                      <span class="product-item-price-final">
                        ${formattedPrice}
                      </span>
                      <span class="product-item-price-comp-value text-muted ms-1">
                        ${formattedComparePrice} ${config.productCompValue}
                      </span>
                    `
                  : `
                    <span class="product-item-price-final">
                      ${formattedPrice}
                    </span>
                  `
              }
            </p>

              ${fakeDiscountResult.shouldShow ? fakeDiscountResult.messageHTML : ""}

              ${promoTextResult.shouldShow ? promoTextResult.promoTextHTML : ""}

              ${swatchesResult.shouldShow ? swatchesResult.swatchesHTML : ""}
            </div>
          </div>
          
          ${options.renderAddToCartMobile ? options.renderAddToCartMobile(product) : ""}
        </div>
      </li>
    `;
  }

  /**
   * Get badge position for the active badge
   * @private
   * @param {object} product - Product data
   * @param {boolean} isOnSale - Whether product is on sale
   * @param {object} config - Configuration object
   * @returns {string} Badge position ('top' or 'bottom')
   */
  static _getBadgePosition(product, isOnSale, config) {
    const data = product.constructorData?.data || product.constructorData || product.data || {};

    const customBadgeSettings = this.getThemeConfig("customBadge");
    if (customBadgeSettings?.globalEnabled && customBadgeSettings?.marketSettings?.enabled) {
      return customBadgeSettings.position || "top";
    }

    if (data.preorder_tag) {
      const preorderSettings = this.getThemeConfig("preorderBadge");
      if (preorderSettings?.enabled) {
        return preorderSettings.position || "top";
      }
    }

    if (data.new_preorder_tag) {
      const newPreorderSettings = this.getThemeConfig("newPreorderBadge");
      if (newPreorderSettings?.enabled) {
        return newPreorderSettings.position || "top";
      }
    }

    const promoBadgeSettings = this.getThemeConfig("promoBadge");
    if (promoBadgeSettings?.enabled && isOnSale) {
      return promoBadgeSettings.position || "top";
    }

    const commercialBadgeSettings = this.getThemeConfig("commercialBadge");
    if (commercialBadgeSettings?.enabled) {
      const hasCommercialBadge = data.waterproof_tag || data.new_tag || data.bestseller_tag || data.low_inventory_tag;
      if (hasCommercialBadge) {
        return commercialBadgeSettings.position || "top";
      }
    }

    if (config.showSaleBadge && isOnSale) {
      return config.saleBadgePosition || "top";
    }

    return "top";
  }

  /**
   * Get default configuration for product card rendering
   * @private
   * @returns {object} Default configuration
   */
  static _getDefaultConfig() {
    const imgOrientation = this.getThemeConfig("productCard.imgOrientation") || "square";
    const dimensions = this.getImageDimensions(imgOrientation);

    return {
      imgWidth: dimensions.width,
      imgHeight: dimensions.height,
      aspectRatio: `${dimensions.width}/${dimensions.height}`,
      cardStyle: this.getThemeConfig("productCard.style") || "",
      spacingMobile: this.getThemeConfig("productCard.spacingMobile") || "p-2",
      spacingDesktop: this.getThemeConfig("productCard.spacingDesktop") || "p-lg-3",
      imgThumbnail: this.getThemeConfig("productCard.imgThumbnail") || "",
      titleFontSizeMobile: this.getThemeConfig("productCard.titleFontSizeMobile") || "",
      titleFontSizeDesktop: this.getThemeConfig("productCard.titleFontSizeDesktop") || "",
      titleTruncate: this.getThemeConfig("productCard.titleTruncate") ? "text-truncate-2" : "",
      showWishlistBtn: this.getThemeConfig("productCard.showWishlistBtn") || false,
      showRating: this.getThemeConfig("productCard.showRating") || false,
      wishlistEnable: this.getThemeConfig("wishlist.enable") || false,
      wishlistAddText: this.getThemeConfig("wishlist.addText") || "Add to wishlist",
      wishlistRemoveText: this.getThemeConfig("wishlist.removeText") || "Remove from wishlist",
      showSaleBadge: this.getThemeConfig("productCard.showSaleBadge") || false,
      saleBadgeColor: this.getThemeConfig("productCard.saleBadgeColor") || "badge-danger",
      saleBadgePosition: this.getThemeConfig("productCard.saleBadgePosition") || "top",
      productSave: this.getThemeConfig("productCard.translations.save") || "Save",
      productPriceRegular: this.getThemeConfig("productCard.translations.regularPrice") || "Regular price",
      productPriceSale: this.getThemeConfig("productCard.translations.salePrice") || "Sale price",
    };
  }

  /**
   * Create a debounced function
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  static debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Create a throttled function
   * @param {Function} func - Function to throttle
   * @param {number} limit - Time limit in milliseconds
   * @returns {Function} Throttled function
   */
  static throttle(func, limit) {
    let inThrottle;
    return function executedFunction(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  /**
   * Safely parse JSON with fallback
   * @param {string} jsonString - JSON string to parse
   * @param {any} fallback - Fallback value if parsing fails
   * @returns {any} Parsed JSON or fallback
   */
  static safeJSONParse(jsonString, fallback = null) {
    try {
      return JSON.parse(jsonString);
    } catch {
      return fallback;
    }
  }

  /**
   * Generate unique ID
   * @param {string} prefix - Optional prefix for the ID
   * @returns {string} Unique ID
   */
  static generateId(prefix = "") {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 8);
    return prefix ? `${prefix}-${timestamp}-${randomStr}` : `${timestamp}-${randomStr}`;
  }

  /**
   * Generate filters HTML from Constructor.io facets data
   * @param {Array} facets - Constructor.io facets array
   * @param {Object} currentFilters - Currently applied filters
   * @returns {string} Generated filters HTML
   */
  static generateFiltersFromFacets(facets, currentFilters = {}) {
    if (!facets || facets.length === 0) {
      return '<p class="text-muted">No filters available</p>';
    }

    const priceFilters = facets.filter((f) => f.type === "range" && f.name === "price");
    const otherFilters = facets.filter((f) => {
      const opts = f.options || f.values || [];
      return f.name !== "price" && opts.length > 0;
    });

    const allFilters = [...priceFilters, ...otherFilters];

    if (allFilters.length === 0) {
      return '<p class="text-muted">No filters available</p>';
    }

    return allFilters
      .map((facet, index) => {
        const isExpanded = index < 2;

        if (facet.type === "range" && facet.name === "price") {
          return this._generatePriceRangeFilter(facet, currentFilters, isExpanded);
        }

        return this._generateCheckboxFilter(facet, currentFilters, isExpanded);
      })
      .join("");
  }

  /**
   * Generate price range filter HTML
   * @private
   */
  static _generatePriceRangeFilter(facet, currentFilters, isExpanded) {
    const facetMin = parseFloat(facet.min.toFixed(2));
    const facetMax = parseFloat(facet.max.toFixed(2));

    let currentMin = currentFilters.price_min ?? facetMin;
    let currentMax = currentFilters.price_max ?? facetMax;

    if (currentFilters.price && typeof currentFilters.price === "string" && currentFilters.price.includes("-")) {
      const parts = currentFilters.price.split("-");
      if (parts[0]) currentMin = parseFloat(parts[0]);
      if (parts[1]) currentMax = parseFloat(parts[1]);
    }

    const currencySymbol = window.Shopify?.currency?.symbol || "$";

    return `
      <details class="filter-group mb-4" ${isExpanded ? "open" : ""}>
        <summary class="filter-toggle py-3 cursor-pointer list-none">
          <span class="h6 mb-0">${this.sanitizeHTML(facet.display_name || facet.name)}</span>
        </summary>
        <div class="filter-options pt-3">
          <div class="price-range-inputs row mx-n2">
            <div class="col-6 px-2">
              <div class="input-group">
                <span class="input-group-text px-3">${currencySymbol}</span>
                <input
                  class="form-control constructor-price-input"
                  name="price_min"
                  value="${currentMin}"
                  type="number"
                  min="${facetMin}"
                  max="${facetMax}"
                  step="0.01"
                  placeholder="${facetMin}"
                  data-filter-name="price_min"
                  aria-label="Minimum price"
                >
              </div>
            </div>
            <div class="col-6 px-2">
              <div class="input-group">
                <span class="input-group-text px-3">${currencySymbol}</span>
                <input
                  class="form-control constructor-price-input"
                  name="price_max"
                  value="${currentMax}"
                  type="number"
                  min="${facetMin}"
                  max="${facetMax}"
                  step="0.01"
                  placeholder="${facetMax}"
                  data-filter-name="price_max"
                  aria-label="Maximum price"
                >
              </div>
            </div>
          </div>
          <div class="mt-3">
            <small class="text-muted">Range: ${currencySymbol}${facetMin} - ${currencySymbol}${facetMax}</small>
          </div>
        </div>
      </details>
    `;
  }

  /**
   * Generate checkbox filter HTML
   * @private
   */
  static _generateCheckboxFilter(facet, currentFilters, isExpanded) {
    const options = (facet.options || facet.values || [])
      .map((opt) => {
        const isChecked = currentFilters[facet.name]?.includes(opt.value) || opt.selected;
        return `
          <label class="d-flex align-items-center mb-2 cursor-pointer">
            <input type="checkbox" 
                   class="constructor-filter-checkbox me-2"
                   data-filter-name="${this.sanitizeHTML(facet.name)}"
                   data-filter-value="${this.sanitizeHTML(opt.value)}"
                   ${isChecked ? "checked" : ""}>
            <span class="small">${this.sanitizeHTML(opt.display_name || opt.value)} (${opt.count || 0})</span>
          </label>
        `;
      })
      .join("");

    return `
      <details class="filter-group mb-4" ${isExpanded ? "open" : ""}>
        <summary class="filter-toggle py-3 cursor-pointer list-none">
          <span class="h6 mb-0">${this.sanitizeHTML(facet.display_name || facet.name)}</span>
        </summary>
        <div class="filter-options pt-3">
          ${options}
        </div>
      </details>
    `;
  }

  /**
   * Generate sort options HTML from Constructor.io sort_options data
   * @param {Array} sortOptions - Constructor.io sort_options array
   * @returns {string} Generated sort dropdown HTML
   */
  static generateSortOptionsFromData(sortOptions) {
    if (!sortOptions || sortOptions.length === 0) {
      return "";
    }

    return sortOptions
      .map(
        (option) => `
        <div class="dropdown-item ${option.status === "selected" ? "selected" : ""}"
             data-value="${option.sort_by}"
             data-order="${option.sort_order}">
          ${option.display_name}
        </div>
      `
      )
      .join("");
  }

  /**
   * Sanitize HTML to prevent XSS
   * @param {string} str - String to sanitize
   * @returns {string} Sanitized string
   */
  static sanitizeHTML(str) {
    if (!str) return "";
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }
}

if (typeof window !== "undefined") {
  window.ConstructorUtils = ConstructorUtils;

  // Inject responsive styles for Constructor.io products
  (function injectConstructorResponsiveStyles() {
    if (document.getElementById("constructor-utils-responsive-styles")) return;

    const style = document.createElement("style");
    style.id = "constructor-utils-responsive-styles";
    style.textContent = `
      /* Desktop: center content (default) */
      [data-cnstrc-item] .firstline-container,
      [data-cnstrc-item] .firstline-container > div {
        text-align: center;
      }

      [data-cnstrc-item] .product-item-title,
      [data-cnstrc-item] .product-item-title a {
        text-align: center;
      }

      [data-cnstrc-item] .product-item-price,
      [data-cnstrc-item] [data-product-item-price] {
        text-align: center;
      }

      [data-cnstrc-item] .swatches-container {
        text-align: center;
      }

      [data-cnstrc-item] .color-swatches {
        display: flex;
        justify-content: center;
        align-items: center;
      }

      [data-cnstrc-item] .fake-discount-message {
        text-align: center;
      }

      /* Mobile: left-align content */
      @media (max-width: 767px) {
        [data-cnstrc-item] .firstline-container,
        [data-cnstrc-item] .firstline-container > div {
          text-align: left !important;
        }

        [data-cnstrc-item] .product-item-title,
        [data-cnstrc-item] .product-item-title a {
          text-align: left !important;
        }

        [data-cnstrc-item] .product-item-price,
        [data-cnstrc-item] [data-product-item-price] {
          text-align: left !important;
        }

        [data-cnstrc-item] .swatches-container {
          text-align: left !important;
        }

        [data-cnstrc-item] .color-swatches {
          display: flex !important;
          justify-content: flex-start !important;
          align-items: center !important;
          padding-left: 0 !important;
          margin-left: 0 !important;
        }

        [data-cnstrc-item] .color-swatches li {
          margin-left: 0 !important;
        }

        [data-cnstrc-item] .color-swatches li:first-child {
          padding-left: 0 !important;
        }

        [data-cnstrc-item] .fake-discount-message {
          text-align: left !important;
        }
      }
    `;
    document.head.appendChild(style);
  })();
}
